import PropTypes from 'prop-types';
import React from 'react';
import { convertToRaw, Draft } from '@ublend-npm/th90-editor';
import { EditorContainer } from './SectionContent.styled';
import Editor from '../../../common/EditorPlus/EditorPlus.container';
import { MATERIAL_EDITOR_PLACEHOLDER } from '../../../../../constants/texts';
import { atLeastOneUnfinishedBlock } from '../../../../../utils/editorUtils';

const empty = (editor) => {
  if (!editor) return true;
  if (!editor.editor) return true;

  const state = editor.editor.getEditorState();
  if (!state) return true;

  const contentState = state.getCurrentContent();
  if (!contentState) return true;

  return editor.isEmpty(contentState);
};

const toCustomRaw = (block) => {
  const data = block.getData();
  return {
    data: {
      type: data.get('type'),
      readyForAction: data.get('readyForAction'),
      url: data.get('url'),
    },
  };
};

const spacingBlock = () => ({
  key: Draft.genKey(),
  text: '',
  type: 'atomic',
  depth: 0,
  inlineStyleRanges: [],
  entityRanges: [],
  data: {
    type: 'spacing',
  },
});

const emptyBlock = () => ({
  key: Draft.genKey(),
  text: '',
  type: 'unstyled',
  depth: 0,
  inlineStyleRanges: [],
  entityRanges: [],
  data: {},
});

const contentToEditorState = (editable, data) => {
  const lastBlock =
    data.blocks.length !== 0 ? data.blocks[data.blocks.length - 1] : {};
  const shouldAddEditBlocks =
    editable &&
    lastBlock.type === 'atomic' &&
    lastBlock.data.type !== 'spacing';
  if (shouldAddEditBlocks) {
    return {
      ...data,
      blocks: [...data.blocks, spacingBlock(), emptyBlock()],
    };
  }

  return data;
};

class SectionContent extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      emptyContent: false,
    };
    this.container = undefined;
    this.editor = undefined;
    this.handleChange = this.handleChange.bind(this);
    this.handleContainerClick = this.handleContainerClick.bind(this);
    this.handleFileIgnore = this.handleFileIgnore.bind(this);
    this.setContainer = this.setContainer.bind(this);
    this.setEditor = this.setEditor.bind(this);
  }

  componentDidUpdate(prevProps) {
    const editableChanged = prevProps.editable !== this.props.editable;
    const sectionIdChanged = prevProps.sectionId !== this.props.sectionId;
    const versionIdChanged = prevProps.versionId !== this.props.versionId;
    if (editableChanged || sectionIdChanged || versionIdChanged) {
      this.editor.import(
        contentToEditorState(this.props.editable, this.props.content)
      );
    }
  }

  setContainer(reference) {
    this.container = reference;
  }

  setEditor(reference) {
    this.editor = reference;
  }

  handleChange(editorState) {
    this.setState({ emptyContent: empty(this.editor) });
    const blocks = editorState
      .getCurrentContent()
      .getBlocksAsArray()
      .map(toCustomRaw);
    this.props.setUnfinished(atLeastOneUnfinishedBlock(blocks));
    if (this.ignoreNextOnChangeCall) {
      this.ignoreNextOnChangeCall = false;
    }
  }

  handleFileIgnore(callback) {
    this.ignoreFileUpload = callback;
    this.ignoreNextOnChangeCall = true;
    callback(!this.props.editable);
  }

  handleContainerClick() {
    if (!this.editor) return;

    this.editor.focus();
  }

  focus() {
    if (!this.editor) return;
    this.editor.focus();
  }

  export() {
    return this.editor.export();
  }

  render() {
    const { editable, content, ...otherProps } = this.props;
    return (
      <EditorContainer
        innerRef={this.setContainer}
        hoverable={editable && this.state.emptyContent}
        onClick={this.handleContainerClick}
        data-testid="editor-container"
      >
        <Editor
          wrappedRef={this.setEditor}
          type="material"
          readOnly={!editable}
          initialEditorState={content}
          isInstructor={editable}
          placeholder={MATERIAL_EDITOR_PLACEHOLDER}
          onChange={this.handleChange}
          pluginsConfigs={{
            textFormatter: {
              verticalOffset: 15,
            },
            file: {
              ignore: this.handleFileIgnore,
              showProgress: true,
            },
          }}
          {...otherProps}
        />
      </EditorContainer>
    );
  }
}

SectionContent.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  content: PropTypes.object.isRequired,
  editable: PropTypes.bool,
  trackEvent: PropTypes.func,
  sectionId: PropTypes.string.isRequired,
  setUnfinished: PropTypes.func,
  versionId: PropTypes.string,
};

SectionContent.defaultProps = {
  versionId: null,
  editable: false,
  setUnfinished: () => null,
  trackEvent: () => null,
};

export default SectionContent;
